import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSent(true);
        toast.success("Password reset email sent!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reset email");
      }
    } catch (_error) {
      console.error("Forgot password _error:", _error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#333333] p-12 flex-col justify-between">
        <div>
          <Link to="/login" className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-semibold text-white leading-tight">
            Reset Your
            <span className="block text-[#39FF14]">Password</span>
          </h1>
          <p className="text-neutral-300 text-lg max-w-md">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
          <div>
            <p className="text-sm font-semibold text-white leading-tight">My Biz Consulting</p>
            <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/login" className="inline-flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to login</span>
            </Link>
          </div>
          
          {sent ? (
            <div className="text-center space-y-6" data-testid="reset-email-sent">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Check Your Email</h2>
                <p className="text-neutral-600">
                  We've sent password reset instructions to <strong>{email}</strong>
                </p>
              </div>
              <p className="text-sm text-neutral-500">
                Didn't receive the email? Check your spam folder or{" "}
                <button 
                  onClick={() => setSent(false)} 
                  className="text-[#39FF14] hover:text-[#32E010] font-medium"
                >
                  try again
                </button>
              </p>
              <Link to="/login">
                <Button className="w-full h-12 bg-neutral-700 hover:bg-neutral-600 text-white font-medium mt-4">
                  Return to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center lg:text-left">
                <h2 className="text-3xl font-semibold text-neutral-900 mb-2">Forgot Password</h2>
                <p className="text-neutral-600">Enter your email to receive reset instructions</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-neutral-700 hover:bg-neutral-600 text-white font-medium touch-manipulation"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>

              <p className="text-center text-neutral-600">
                Remember your password?{" "}
                <Link to="/login" className="text-[#39FF14] hover:text-[#32E010] font-medium">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
